<template>
  <section class="form-section am-section">
    <div class="section-header">
      <div class="section-icon">
        <font-awesome-icon :icon="['fas', 'user-check']" class="text-lg" />
      </div>
      <div class="am-section-heading">
        <div class="am-title-row">
          <h2 class="section-title">Account Managers</h2>
          <span v-if="modelValue.length" class="am-count">{{ modelValue.length }}</span>
        </div>
        <p class="am-section-subtitle">
          Internal staff responsible for managing this partnership.
        </p>
      </div>
    </div>

    <div v-if="!modelValue.length" class="am-empty">
      <div class="am-empty-icon">
        <font-awesome-icon :icon="['fas', 'user-check']" />
      </div>
      <h3 class="am-empty-title">No account managers yet</h3>
      <p class="am-empty-text">
        Add internal staff with name, title, entity, mobile, and email.
      </p>
      <button type="button" class="am-btn-primary" @click.stop="addMember">
        <icon-plus class="h-4 w-4"></icon-plus>
        <span>Add account manager</span>
      </button>
    </div>

    <div v-else class="am-list">
      <article
        v-for="(item, index) in modelValue"
        :key="'am-' + index"
        class="am-card"
      >
        <header class="am-card-header">
          <div class="am-card-header-left">
            <span class="am-badge">{{ index + 1 }}</span>
            <span class="am-card-label">Account Manager {{ index + 1 }}</span>
          </div>
          <button
            type="button"
            class="am-btn-remove"
            title="Remove account manager"
            @click.stop="removeMember(index)"
          >
            <font-awesome-icon :icon="['fas', 'trash-alt']" class="am-btn-remove-icon" />
            <span>Remove</span>
          </button>
        </header>

        <div class="am-card-body">
          <div class="am-user-picker">
            <label class="form-label">Find staff user <span class="required">*</span></label>
            <div class="am-search-wrap">
              <div class="am-search-field">
                <font-awesome-icon :icon="['fas', 'search']" class="am-search-icon" />
                <input
                  type="search"
                  class="form-input am-search-input"
                  :value="item.user_search"
                  placeholder="Search by name, email, or position…"
                  autocomplete="off"
                  @input="onUserSearchInput(index, $event)"
                  @focus="openUserDropdown(index)"
                />
              </div>
              <ul
                v-if="item.show_user_dropdown && item.user_results && item.user_results.length"
                class="am-user-dropdown"
              >
                <li
                  v-for="user in item.user_results"
                  :key="user.id"
                  class="am-user-option"
                  @mousedown.prevent="selectUser(index, user)"
                >
                  <span class="am-user-option-avatar">{{ userInitial(user) }}</span>
                  <span class="am-user-option-body">
                    <span class="am-user-option-name">{{ user.name }}</span>
                    <span class="am-user-option-meta">{{ formatUserMeta(user) }}</span>
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div class="am-fields">
            <div class="form-group am-col-name">
              <label class="form-label">Name <span class="required">*</span></label>
              <input
                type="text"
                v-model.trim="item.name"
                class="form-input form-input-readonly"
                placeholder="Full name"
                readonly
              />
            </div>
            <div class="form-group am-col-title">
              <label class="form-label">Title <span class="required">*</span></label>
              <input
                type="text"
                v-model.trim="item.title"
                class="form-input"
                placeholder="Job title / designation"
              />
            </div>
            <div class="form-group am-col-entity">
              <label class="form-label">Entity <span class="required">*</span></label>
              <input
                type="text"
                v-model.trim="item.entity"
                class="form-input"
                placeholder="Entity / organization"
              />
            </div>
            <div class="form-group am-col-mobile">
              <label class="form-label">Mobile <span class="required">*</span></label>
              <input
                type="text"
                v-model.trim="item.mobile"
                class="form-input"
                placeholder="Mobile number"
                @keypress="allowNumbersOnly"
              />
            </div>
            <div class="form-group am-col-email">
              <label class="form-label">Email <span class="required">*</span></label>
              <input
                type="text"
                v-model.trim="item.email"
                class="form-input"
                placeholder="email@example.com"
              />
            </div>
          </div>
        </div>
      </article>

      <div class="am-footer-actions">
        <button type="button" class="am-btn-add-another" @click.stop="addMember">
          <icon-plus class="h-4 w-4"></icon-plus>
          <span>Add another account manager</span>
        </button>
      </div>
    </div>

    <transition name="form-fade" mode="out-in">
      <p v-if="displayError" key="am-err" class="form-error-msg form-error-msg-center">{{ displayError }}</p>
    </transition>
  </section>
</template>

<script>
import gql from "graphql-tag";
import IconPlus from "../../components/icons/IconPlus.vue";

var SEARCH_USERS_QUERY = gql`
  query SearchPartnerUsers($search: String, $first: Int) {
    searchPartnerUsers(search: $search, first: $first) {
      id
      name
      email
      phone_number
      position
      entity
    }
  }
`;

function emptyMember() {
  return {
    user_id: null,
    user_search: "",
    user_results: [],
    show_user_dropdown: false,
    search_timer: null,
    name: "",
    title: "",
    entity: "",
    mobile: "",
    email: "",
  };
}

export default {
  name: "AccountManagersEditor",
  components: {
    IconPlus: IconPlus,
  },
  props: {
    modelValue: {
      type: Array,
      default: function () {
        return [];
      },
    },
    errorMessage: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue"],
  data: function () {
    return {
      localError: "",
      documentClickHandler: null,
    };
  },
  computed: {
    displayError: function () {
      if (this.localError) {
        return this.localError;
      }
      return this.errorMessage || "";
    },
  },
  watch: {
    errorMessage: function (val) {
      if (val) {
        this.localError = "";
      }
    },
  },
  mounted: function () {
    var self = this;
    this.documentClickHandler = function (event) {
      self.handleDocumentClick(event);
    };
    document.addEventListener("mousedown", this.documentClickHandler);
  },
  beforeUnmount: function () {
    if (this.documentClickHandler) {
      document.removeEventListener("mousedown", this.documentClickHandler);
      this.documentClickHandler = null;
    }
    var list = this.modelValue || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].search_timer) {
        clearTimeout(list[i].search_timer);
      }
    }
  },
  methods: {
    emitNext: function (next) {
      this.$emit("update:modelValue", next);
    },
    cloneList: function () {
      return (this.modelValue || []).slice();
    },
    handleDocumentClick: function (event) {
      var target = event && event.target ? event.target : null;
      if (target && typeof target.closest === "function" && target.closest(".am-search-wrap")) {
        return;
      }
      this.closeAllUserDropdowns();
    },
    closeAllUserDropdowns: function () {
      var list = this.modelValue || [];
      var changed = false;
      var next = [];
      for (var i = 0; i < list.length; i++) {
        var row = list[i];
        if (row && row.show_user_dropdown) {
          next.push(
            Object.assign({}, row, {
              show_user_dropdown: false,
            })
          );
          changed = true;
        } else {
          next.push(row);
        }
      }
      if (changed) {
        this.emitNext(next);
      }
    },
    isMemberComplete: function (row) {
      if (!row) {
        return false;
      }
      if (!row.user_id) {
        return false;
      }
      if (!row.name || !row.title || !row.entity || !row.mobile || !row.email) {
        return false;
      }
      return true;
    },
    addMember: function () {
      var list = this.modelValue || [];
      if (list.length > 0) {
        for (var i = 0; i < list.length; i++) {
          if (!this.isMemberComplete(list[i])) {
            this.localError =
              "Please complete all required fields for existing account managers before adding another.";
            return;
          }
        }
      }
      this.localError = "";
      var next = this.cloneList();
      next.push(emptyMember());
      this.emitNext(next);
    },
    removeMember: function (index) {
      var next = this.cloneList();
      if (next[index] && next[index].search_timer) {
        clearTimeout(next[index].search_timer);
      }
      next.splice(index, 1);
      this.localError = "";
      this.emitNext(next);
    },
    clearMemberData: function (index) {
      var next = this.cloneList();
      var prev = next[index] || {};
      if (prev.search_timer) {
        clearTimeout(prev.search_timer);
      }
      next[index] = emptyMember();
      this.localError = "";
      this.emitNext(next);
    },
    updateField: function (index, key, value) {
      var next = this.cloneList();
      var row = Object.assign({}, next[index]);
      row[key] = value;
      next[index] = row;
      this.emitNext(next);
    },
    allowNumbersOnly: function (event) {
      var charCode = event.which ? event.which : event.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 43) {
        event.preventDefault();
      }
    },
    userInitial: function (user) {
      if (!user || !user.name) {
        return "?";
      }
      return String(user.name).charAt(0).toUpperCase();
    },
    formatUserMeta: function (user) {
      var parts = [];
      if (user.position) {
        parts.push(user.position);
      }
      if (user.entity) {
        parts.push(String(user.entity).replace(/ATHS/g, "ATS"));
      }
      if (user.email) {
        parts.push(user.email);
      }
      return parts.join(" · ");
    },
    openUserDropdown: function (index) {
      this.updateField(index, "show_user_dropdown", true);
      var row = this.modelValue[index];
      if (row && (!row.user_results || row.user_results.length === 0)) {
        this.searchUsers(index, row.user_search || "");
      }
    },
    onUserSearchInput: function (index, event) {
      var value = event && event.target ? event.target.value : "";

      if (!value || !String(value).trim()) {
        this.clearMemberData(index);
        return;
      }

      var next = this.cloneList();
      var row = Object.assign({}, next[index]);
      row.user_search = value;
      row.show_user_dropdown = true;
      if (row.search_timer) {
        clearTimeout(row.search_timer);
      }
      var self = this;
      row.search_timer = setTimeout(function () {
        self.searchUsers(index, value);
      }, 300);
      next[index] = row;
      this.emitNext(next);
    },
    searchUsers: function (index, search) {
      var self = this;
      this.$apollo
        .query({
          query: SEARCH_USERS_QUERY,
          variables: {
            search: search || null,
            first: 12,
          },
          fetchPolicy: "network-only",
        })
        .then(function (result) {
          var users =
            result && result.data && result.data.searchPartnerUsers
              ? result.data.searchPartnerUsers
              : [];
          var next = self.cloneList();
          if (!next[index]) {
            return;
          }
          var row = Object.assign({}, next[index]);
          row.user_results = users;
          next[index] = row;
          self.emitNext(next);
        })
        .catch(function () {
          var next = self.cloneList();
          if (!next[index]) {
            return;
          }
          var row = Object.assign({}, next[index]);
          row.user_results = [];
          next[index] = row;
          self.emitNext(next);
        });
    },
    selectUser: function (index, user) {
      if (!user) {
        return;
      }
      var next = this.cloneList();
      var row = Object.assign({}, next[index]);
      row.user_id = parseInt(user.id, 10);
      row.user_search = user.name || "";
      row.show_user_dropdown = false;
      row.user_results = [];
      row.name = user.name || "";
      row.title = user.position || row.title || "";
      row.mobile = user.phone_number || row.mobile || "";
      row.email = user.email || row.email || "";
      if (user.entity) {
        var entityRaw = String(user.entity).split(",")[0].trim();
        row.entity = entityRaw;
      }
      next[index] = row;
      this.localError = "";
      this.emitNext(next);
    },
  },
};
</script>

<style scoped>
.form-section {
  margin-bottom: 1.5rem;
  padding: 1.35rem 1.5rem 1.5rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.section-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f1f5f9;
}

.section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border-radius: 0.5rem;
  background: #e0f2fe;
  color: #0c4a6e;
}

.section-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.01em;
}

.am-section-heading {
  min-width: 0;
  flex: 1;
}

.am-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.am-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.4rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #0c4a6e;
  background: #e0f2fe;
}

.am-section-subtitle {
  margin: 0.2rem 0 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: #64748b;
}

.am-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.25rem 1.5rem;
  border-radius: 0.75rem;
  border: 1px dashed #bae6fd;
  background: linear-gradient(180deg, #f0f9ff 0%, #ffffff 100%);
}

.am-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  margin-bottom: 0.75rem;
  border-radius: 0.75rem;
  background: #e0f2fe;
  color: #0c4a6e;
  font-size: 1.15rem;
}

.am-empty-title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1e293b;
}

.am-empty-text {
  margin: 0.35rem 0 1.1rem;
  max-width: 22rem;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #64748b;
}

.am-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.6rem 1.15rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #fff;
  background: #0c4a6e;
  border: none;
  border-radius: 0.55rem;
  cursor: pointer;
  box-shadow: 0 6px 16px -8px rgba(12, 74, 110, 0.5);
  transition: background 0.2s, transform 0.15s;
}

.am-btn-primary:hover {
  background: #075985;
  transform: translateY(-1px);
}

.am-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.am-card {
  border: 1px solid #e2e8f0;
  border-radius: 0.85rem;
  background: #fff;
  overflow: visible;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.am-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.85rem 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #eef2f7;
  border-radius: 0.85rem 0.85rem 0 0;
}

.am-card-header-left {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
}

.am-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 0.45rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #0c4a6e;
  background: #e0f2fe;
  flex-shrink: 0;
}

.am-card-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #334155;
}

.am-btn-remove {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.7rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #b91c1c;
  background: #fff;
  border: 1px solid #fecaca;
  border-radius: 0.45rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.am-btn-remove:hover {
  background: #fef2f2;
  border-color: #fca5a5;
}

.am-btn-remove-icon {
  font-size: 0.7rem;
}

.am-card-body {
  padding: 0;
  overflow: visible;
  position: relative;
  z-index: 1;
}

.am-user-picker {
  padding: 1rem 1rem 0;
  overflow: visible;
  position: relative;
  z-index: 5;
}

.am-search-wrap {
  position: relative;
  z-index: 6;
}

.am-search-field {
  position: relative;
}

.am-search-icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.8rem;
  pointer-events: none;
}

.am-search-input {
  padding-left: 2.35rem !important;
}

.am-user-dropdown {
  position: absolute;
  z-index: 80;
  left: 0;
  right: 0;
  top: calc(100% + 0.3rem);
  margin: 0;
  padding: 0.35rem 0;
  list-style: none;
  max-height: 15rem;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.65rem;
  box-shadow: 0 12px 28px -10px rgba(15, 23, 42, 0.28);
}

.am-user-option {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.6rem 0.9rem;
  cursor: pointer;
}

.am-user-option:hover {
  background: #f8fafc;
}

.am-user-option-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #0c4a6e;
  background: #e0f2fe;
  flex-shrink: 0;
}

.am-user-option-body {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.am-user-option-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0f172a;
}

.am-user-option-meta {
  font-size: 0.75rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.am-fields {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.9rem;
  padding: 1rem;
  position: relative;
  z-index: 0;
}

@media (min-width: 640px) {
  .am-fields {
    grid-template-columns: 1fr 1fr;
  }
}

@media (min-width: 1024px) {
  .am-fields {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .am-col-name {
    grid-column: span 2;
  }

  .am-col-title {
    grid-column: span 2;
  }

  .am-col-entity {
    grid-column: span 2;
  }

  .am-col-mobile {
    grid-column: span 2;
  }

  .am-col-email {
    grid-column: span 4;
  }
}

.form-input-readonly {
  background: #f8fafc;
  color: #475569;
}

.am-footer-actions {
  display: flex;
  justify-content: center;
  padding-top: 0.15rem;
}

.am-btn-add-another {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0c4a6e;
  background: #fff;
  border: 1px dashed #7dd3fc;
  border-radius: 0.55rem;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.am-btn-add-another:hover {
  border-style: solid;
  border-color: #0284c7;
  background: #f0f9ff;
  color: #075985;
}

.form-label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
}

.required {
  color: #dc2626;
}

.form-input {
  display: block;
  width: 100%;
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  border: 1px solid #e2e8f0;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-input::placeholder {
  color: #94a3b8;
}

.form-input:focus {
  outline: none;
  border-color: #0284c7;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

.form-error-msg {
  margin-top: 0.75rem;
  font-size: 0.75rem;
  color: #e11d48;
}

.form-error-msg-center {
  text-align: center;
}

.form-fade-enter-active,
.form-fade-leave-active {
  transition: all 0.3s ease;
}

.form-fade-enter-from,
.form-fade-leave-to {
  opacity: 0;
}
</style>
